import { http } from '../../../configuration/api';
import { ApiResponse, ExpressOffer, Request } from '../types';

// Re-export interface for type compatibility
export interface OffersListResponse {
	data: any[];
	pagination: {
		total_pages: number;
		current_page: number;
		total_items: number;
	};
}

export type OfferStatus = 'active' | 'completed' | 'archived';

// API Response interfaces for offer detail fetching
interface RequestDetailResponse {
	success: boolean;
	data: {
		id: string;
		farmer_id: string;
		specialist_id: string;
		services_ids: string[];
		request_type: string;
		start_date: string;
		end_date: string;
		crop_id: string;
		area: number;
		unit: string;
		location_name: string;
		latitude: number;
		longitude: number;
		communication_language: string[];
		problem_description: string;
		specialist_connection_code: string | null;
		offer_id: string | null;
		payment_status: string;
		request_status: string;
		request_files: any[];
		request_id: string;
		created_at: string;
		updated_at: string;
	};
	error: string;
}

interface UserDataResponse {
	success: boolean;
	data: {
		id: string;
		user_id: string;
		first_name: string;
		last_name: string;
		profile_image: string;
		country: string;
		unicode?: {
			alpha_two: string;
			alpha_three: string;
		};
	};
}

interface ServiceDataResponse {
	success: boolean;
	data: Array<{
		id: string;
		name: string;
		price: number;
		duration: number;
		currency: string;
	}>;
}

interface CropDataResponse {
	success: boolean;
	data: Array<{
		id: string;
		name: string;
		image_url: string;
		country_code: string;
	}>;
}

interface LanguageDataResponse {
	success: boolean;
	data: Array<{
		id: string;
		name: string;
	}>;
}

/**
 * Fetch requests based on status with pagination
 */
export const getRequests = async (
	status: OfferStatus = 'active',
	page: number = 1,
	limit: number = 10
): Promise<{ data: { requests: Request[] }; pagination?: any }> => {
	try {
		const response = await http.get<any>(
			'requests',
			`farmer-request-and-offer/express?page=${page}&limit=${limit}&status=${status}`
		);

		return {
			data: {
				requests: Array.isArray(response.data.data?.data) ? response.data.data.data : [],
			},
			pagination: {
				total_pages: response.data.data?.totalPages || 1,
				current_page: response.data.data?.currentPage || 1,
				total_items: response.data.data?.totalData || 0,
				has_next_page: response.data.data?.hasNextPage || false,
			},
		};
	} catch (error: any) {
		console.error(`Error fetching ${status} requests:`, error);
		throw error;
	}
};

/**
 * Fetch offers for a specific request
 */
export const getOffersForRequest = async (
	requestId: string,
	serviceId?: string
): Promise<{ data: { offers: any[] } }> => {
	try {
		const url = serviceId 
			? `farmer-offers?request_id=${requestId}&service_id=${serviceId}`
			: `farmer-offers?request_id=${requestId}`;
			
		const response = await http.get<ApiResponse<any>>(
			'requests',
			url
		);

		console.log('API Response:', response.data);
		
		const apiData = response.data.data;
		const offers = Array.isArray(apiData?.offers) ? apiData.offers : [];
		
		// Log the offers to see what we're getting from the API
		console.log('Original offers from API:', offers);
		
		// Each offer should already have its own end_date in the API response
		// We don't need to modify it, just ensure it's properly passed through
		
		return {
			data: {
				offers: offers,
			},
		};
	} catch (error: any) {
		console.error(`Error fetching offers for request ${requestId}:`, error);
		throw error;
	}
};

/**
 * Fetch request details by ID for farmer (adapted from specialist version)
 */
export const getRequestDetailByIdParallel = async (requestId: string): Promise<ExpressOffer> => {
	try {
		// 1. First, get the main request details using farmer endpoint
		const requestResponse = await http.get<RequestDetailResponse>(
			'requests',
			`farmer-request/by-id/${requestId}`
		);

		if (!requestResponse.data.success) {
			throw new Error(requestResponse.data.error || 'Failed to fetch request details');
		}

		const requestData = requestResponse.data.data;

		// Validate that we have the essential data
		if (!requestData) {
			throw new Error('No request data received');
		}

		// Handle different formats of services_ids
		let servicesIdParam = '';
		if (Array.isArray(requestData.services_ids)) {
			servicesIdParam = requestData.services_ids.join(',');
		} else if (typeof requestData.services_ids === 'string') {
			servicesIdParam = requestData.services_ids;
		} else {
			servicesIdParam = String(requestData.services_ids || '');
		}

		// 2. Create parallel requests for user, service, crop, language data
		const [userResponse, serviceResponse, cropsResponse, languagesResponse] = await Promise.all([
			http.get<UserDataResponse>('users', `request-user/${requestData.farmer_id}`),
			http.get<ServiceDataResponse>('business-services', `list/get-by-id/?ids=${servicesIdParam}`),
			http.get<CropDataResponse>('crops', 'list/'),
			http.get<LanguageDataResponse>('languages', 'list/'),
		]);

		// 3. Process the data with null checks
		const userData = userResponse.data.data;
		const serviceData = serviceResponse.data.data || [];

		// Find the crop data by crop_id with null check
		const allCrops = cropsResponse.data.data || [];
		const cropData = allCrops.find((crop) => crop && crop.id === requestData.crop_id) || {
			id: requestData.crop_id,
			name: 'Unknown Crop',
			image_url: '',
			country_code: 'en',
		};

		// Find the language data from language IDs with null checks
		const allLanguages = languagesResponse.data.data || [];
		const languageData = (requestData.communication_language || [])
			.filter((langId) => langId != null) // Filter out null/undefined language IDs
			.map((langId) => {
				const language = allLanguages.find((lang) => lang && lang.id === langId);
				return language || { id: langId, name: 'Unknown Language' };
			});

		// 4. Transform the data to match ExpressOffer structure
		const expressOffer: ExpressOffer = {
			id: requestData.id,
			f100_url: '',
			crops: [
				{
					id: cropData.id,
					name: cropData.name,
					image_url: cropData.image_url,
					country_code: cropData.country_code,
				},
			],
			farmer: {
				id: userData.id,
				user_id: userData.user_id,
				full_name: `${userData.first_name} ${userData.last_name}`,
				profile_image_url: userData.profile_image,
				country: {
					id: '',
					name: userData.country,
					alpha_two: userData.unicode?.alpha_two || '',
					alpha_three: userData.unicode?.alpha_three || '',
				},
			},
			services: serviceData
				.filter((service) => service != null) // Filter out null services
				.map((service) => ({
					id: service.id,
					name: service.name || 'Unknown Service',
					price: service.price || 0,
					duration: String(service.duration || 0), // Keep as string but ensure it's not undefined
				})),
			total_price: serviceData.reduce((sum, service) => sum + (service?.price || 0), 0),
			currency: serviceData[0]?.currency || 'EUR',
			farm_details: {
				area: requestData.area,
				unit: requestData.unit,
				location: {
					address: requestData.location_name,
					latitude: requestData.latitude,
					longitude: requestData.longitude,
				},
			},
			communication_languages: languageData,
			comment: requestData.problem_description,
			request_valid_for_days: requestData.end_date 
				? String(Math.max(0, Math.ceil(
					(new Date(requestData.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
				)))
				: '5', // Calculate days remaining or use default
			request_valid_for_timestamp: requestData.end_date,
			service_delivery_days: '5', // Default or calculate from dates
			service_delivery_timestamp: requestData.start_date,
			offer_id: requestData.offer_id || undefined,
			attachments: requestData.request_files,
			request_id: requestData.request_id,
		};

		// 5. Get and add channel ID
		try {
			// Import the chatService directly to avoid circular dependencies
			const { chatService } = await import('services/chat/chatService');
			
			// Check if we have an offer with a specialist ID
			if (requestData.offer_id) {
				// Get the offer details to extract specialist ID
				const offerResponse = await http.get<ApiResponse<any>>(
					'offers',
					`farmer/get-by-id/${requestData.offer_id}`
				);
				
				if (offerResponse.data.success && offerResponse.data.data?.specialist?.id) {
					// Use specialist ID from the offer
					const specialistId = offerResponse.data.data.specialist.id;
					const channelId = await chatService.getChannelIdByRequestAndUserId(
						requestData.request_id,
						specialistId
					);
					expressOffer.channelId = channelId || '';
				} else {
					// Fallback to user_id if we can't get specialist ID
					const channelId = await chatService.getChannelIdByRequestAndUserId(
						requestData.request_id,
						userData.user_id
					);
					expressOffer.channelId = channelId || '';
				}
			} else {
				// No offer ID, use user_id as fallback
				const channelId = await chatService.getChannelIdByRequestAndUserId(
					requestData.request_id,
					userData.user_id
				);
				expressOffer.channelId = channelId || '';
			}
		} catch (error) {
			console.error(`Error fetching channelId for request ${requestData.id}:`, error);
			expressOffer.channelId = '';
		}

		return expressOffer;
	} catch (error: any) {
		console.error('Error fetching combined request details:', error);

		// Provide more specific error messages
		if (error.message?.includes('request-user')) {
			throw new Error('Failed to fetch user data');
		} else if (error.message?.includes('business-services')) {
			throw new Error('Failed to fetch service data');
		} else if (error.message?.includes('crops')) {
			throw new Error('Failed to fetch crop data');
		} else if (error.message?.includes('languages')) {
			throw new Error('Failed to fetch language data');
		} else if (error.message?.includes('farmer-request')) {
			throw new Error('Failed to fetch request details');
		}

		throw error;
	}
};

export async function acceptOffer(offerId: string) {
	let attempts = 0;
	const maxAttempts = 3;
	
	while (attempts < maxAttempts) {
		attempts++;
		try {
			console.log(`Accepting offer with ID: ${offerId} (attempt ${attempts}/${maxAttempts})`);
			
			const response = await http.post<ApiResponse<{ redirect_url?: string }>>(
				`offers`,
				`farmer/accept-offer/${offerId}`
			);
			
			console.log(`Accept offer response (attempt ${attempts}):`, response.data);
			
			// If the response contains a redirect_url, it's a paid offer
			// If not, it's a free offer that was accepted directly
			return response.data;
		} catch (error: any) {
			console.error(`Error accepting offer (attempt ${attempts}/${maxAttempts}):`, error);
			
			// If this is the last attempt, throw the error
			if (attempts >= maxAttempts) {
				throw error;
			}
			
			// Otherwise, wait a bit before retrying
			await new Promise(resolve => setTimeout(resolve, 1000));
		}
	}
	
	// This should never be reached due to the throw in the catch block
	throw new Error(`Failed to accept offer after ${maxAttempts} attempts`);
}

// Add function to mark comment as read
export const markCommentAsRead = async (offerId: string) => {
  try {
    const response = await http.put<ApiResponse<any>>(
      'requests',
      `/make-comment-as-read/${offerId}`
    );
    
    return response.data;
  } catch (error) {
    console.error('Error marking comment as read:', error);
    throw error;
  }
};

// Re-export store hook
export { useFarmerExpressOffersStore } from './store';
